"use client";

import { useEffect } from "react";
import { site } from "@/config/site";

/**
 * Runs the small vanilla scripts of the tool pages (DNS lookup, DMARC, mail tester,
 * price calculator, ...). They were written for plain HTML, so:
 *  - helper files in /public/js load once, in order, then
 *  - the page's own code runs after the page markup exists,
 *  - everything is removed again when the page unmounts.
 * `window.SITE` exposes brand config to those scripts.
 */
export function LegacyScripts({ src = [], code }: { src?: string[]; code?: string }) {
  const key = src.join("|");

  useEffect(() => {
    let cancelled = false;
    const added: HTMLScriptElement[] = [];

    (window as unknown as { SITE: unknown }).SITE = {
      name: site.name,
      domain: site.domain,
      url: site.url,
      smtpHost: site.smtp.host,
      accountPath: site.accountPath,
      mailTester: site.mailTester,
      smtp: {
        host: site.smtp.host,
        ports: site.smtp.ports,
        primaryPort: site.smtp.primaryPort,
        security: site.smtp.security,
      },
    };

    const load = (url: string) =>
      new Promise<void>((resolve) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[data-legacy="${url}"]`);
        if (existing) {
          if (existing.dataset.loaded === "1") resolve();
          else existing.addEventListener("load", () => resolve(), { once: true });
          return;
        }
        const el = document.createElement("script");
        el.src = url;
        el.async = false;
        el.dataset.legacy = url;
        el.addEventListener("load", () => {
          el.dataset.loaded = "1";
          resolve();
        });
        el.addEventListener("error", () => resolve());
        document.body.appendChild(el);
      });

    (async () => {
      for (const url of src) await load(url);
      if (cancelled || !code) return;
      const el = document.createElement("script");
      el.text = code;
      document.body.appendChild(el);
      added.push(el);
    })();

    return () => {
      cancelled = true;
      added.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, code]);

  return null;
}
